import debounce from 'lodash/debounce'
import { watch } from 'vue'
import { basemapMarketingName } from '@/services/basemap-service'
import {
  camera,
  getIsIdle,
  terrainExaggeration,
  viewer3d,
} from '@/services/map-service'
import marketingEventService from '@/services/marketing-event-service'
import type Camera from '@/lib/camera'

let toggled3D = false

/**
 * *(AI-generated)*
 * Registers marketing event watchers when the map becomes idle.
 */
export const registerEventsOnIdle = () => {
  if (!getIsIdle) {
    return
  }

  const unwatch = watch(getIsIdle, (isIdle) => {
    if (!isIdle) {
      return
    }

    unwatch()
    report3D()
    reportPitch()
    reportTerrainExaggeration()
  })
}

/**
 * *(AI-generated)*
 * Sets the toggled-3D marketing flag state.
 */
export const setMarketingToggled3D = (toggled: boolean) => {
  toggled3D = toggled
}

/**
 * Report marketing events when 3D is enabled or disabled.
 */
const report3D = () => {
  watch(viewer3d, (is3d) => {
    if (is3d) {
      marketingEventService.publish('3d_enabled', {
        origin:
          marketingEventService.getEventValue('3d_enabled', 'origin') ||
          'keyboard mouse tilt',
        active_basemap: basemapMarketingName.value,
        '3d_exaggeration_level': terrainExaggeration().toFixed(2),
      })
      marketingEventService.clearEvent('3d_enabled')
    } else {
      marketingEventService.publish('3d_disabled', {
        origin:
          marketingEventService.getEventValue('3d_disabled', 'origin') ||
          'keyboard mouse tilt',
        active_basemap: basemapMarketingName.value,
      })
      marketingEventService.clearEvent('3d_enabled')
    }
  })
}

const reportPitchChange = ({ bearing, pitch }: Camera, oldCam: Camera) => {
  if (toggled3D) {
    // Safe to check this last and clear it, since pitch watcher is debounced,
    // so will always trigger after the 3D watcher.
    setMarketingToggled3D(false)
    return
  }

  const pitchSame = oldCam.pitch === pitch
  const noChange = pitchSame && oldCam.bearing === bearing
  const onlyBearing = pitchSame && oldCam.bearing !== bearing
  if (noChange || onlyBearing) {
    return
  }

  marketingEventService.publish('3d_map_tilted', {
    origin: 'keyboard mouse tilt',
  })
}

/**
 * Report marketing events when map tilt is changed.
 */
const debouncedReportPitchChange = debounce(reportPitchChange, 250)

const reportPitch = () => {
  watch(camera, (newCam, oldCam) => {
    if (newCam !== null && oldCam !== null) {
      debouncedReportPitchChange(newCam, oldCam)
    }
  })
}

/**
 * Report marketing events when terrain exaggeration changes.
 */
const reportTerrainExaggeration = () => {
  watch(
    terrainExaggeration,
    debounce((exaggeration) => {
      marketingEventService.publish('3d_exaggeration_modified', {
        active_basemap: basemapMarketingName.value,
        '3d_exaggeration_level': exaggeration.toFixed(2),
      })
    }, 250)
  )
}
